<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContactFieldset',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contact: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contact,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contact');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_p1grbgbhp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_bi8eqf7hw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_p1grbgbhp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_bi8eqf7hw],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_bi8eqf7hw,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_bi8eqf7hw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_bi8eqf7hw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="name"
      :label="pgettext('Contact', 'Name')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="name"
        v-model="contact.name"
        :value="contact.name"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.name =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        autofocus
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="job_title"
      :label="pgettext('Contact', 'Job Title')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="job_title"
        v-model="contact.job_title"
        :value="contact.job_title"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.job_title =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="phone"
      :label="pgettext('Contact', 'Phone Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="phone"
        v-model="contact.phone"
        :value="contact.phone"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.phone =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="mobile"
      :label="pgettext('Contact', 'Mobile Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="mobile"
        v-model="contact.mobile"
        :value="contact.mobile"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.mobile =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="email"
      :label="pgettext('Contact', 'Email')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="email"
        v-model="contact.email"
        :value="contact.email"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.email =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="fax"
      :label="pgettext('Contact', 'Fax')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="fax"
        v-model="contact.fax"
        :value="contact.fax"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.fax =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="notes"
      :label="pgettext('Contact', 'Notes')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="notes"
        v-model="contact.notes"
        :value="contact.notes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contact.notes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
